import {Component, DestroyRef, inject, OnDestroy, OnInit} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {notNull} from '../../../shared/utils';
import {ActivatedRoute} from '@angular/router';
import {catchError, merge, Subscription} from 'rxjs';
import {Language} from '../../../../gql/graphql';
import {ErrorService} from '../../../shared/services/error.service';
import {MessageService} from 'primeng/api';
import {gql} from '../../../../gql';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {models} from '../../../shared/models';
import {
  BooleanCheckboxComponent,
  FormSaveHelper,
  NumberInputComponent,
  TextAreaComponent,
  TextInputComponent,
} from '@vasio-nl/valow';
import {BooleanFieldTypeInfo, FieldInputType} from '@vasio-nl/valow';

const RIGHTS_QUERY = gql(/* GraphQL */ `
 query GeneralNewcomerInformationComponent_rights {
   me {
     id
     managerHasAccessToSensitiveMessages
    }
   }
`);

@Component({
  selector: 'app-general-newcomer-information',
  templateUrl: './general-newcomer-information.component.html',
  styleUrls: ['./general-newcomer-information.component.scss'],
})
export class GeneralNewcomerInformationComponent implements OnInit, OnDestroy {
  languageOptions = Language;
  subscriptions = new Subscription();
  saving = false;
  addressNotFound = false;
  userHasRightsToSensitiveData = false;

  public formContainer = this.formHelperService.getFormContainer(models.NewComer, fb => ({
    id: fb.modelField('id'),
    firstName: fb.modelField('firstName'),
    lastName: fb.modelField('lastName'),
    dateOfBirth: fb.modelField('dateOfBirth'),
    gender: fb.modelField('gender'),
    poVo: fb.modelField('poVo'),
    poGroup: fb.modelField('poGroup'),

    alreadyInNetherlands: fb.modelField('alreadyInNetherlands'),
    arrivalDate: fb.modelField('arrivalDate'),

    alreadyGoingToSchoolInNetherlands: fb.modelField('alreadyGoingToSchoolInNetherlands'),
    currentSchool: fb.modelField('currentSchool'),
    currentSchoolStartDate: fb.modelField('currentSchoolStartDate'),

    disabilitySupport: fb.modelField('disabilitySupport'),
    disabilitySupportExplanationNote: fb.modelField('disabilitySupportExplanationNote'),

    parentFirstName: fb.modelField('parentFirstName'),
    parentLastName: fb.modelField('parentLastName'),
    parentPhone: fb.modelField('parentPhone'),
    parentEmail: fb.modelField('parentEmail'),

    contactFirstName: fb.modelField('contactFirstName'),
    contactLastName: fb.modelField('contactLastName'),
    contactPhone: fb.modelField('contactPhone'),
    contactEmail: fb.modelField('contactEmail'),

    street: fb.modelField('street'),
    houseNumber: fb.modelField('houseNumber'),
    zip: fb.modelField('zip'),
    city: fb.modelField('city'),

    willMoveSoon: fb.modelField('willMoveSoon'),
    newStreet: fb.modelField('newStreet'),
    newHouseNumber: fb.modelField('newHouseNumber'),
    newZip: fb.modelField('newZip'),
    newCity: fb.modelField('newCity'),

    languages: fb.modelField('languages'),
    otherLanguages: fb.modelField('otherLanguages'),
    hasInterpreter: fb.modelField('hasInterpreter'),

    isUkrainian: fb.modelField('isUkrainian'),

    remarks: fb.modelField('remarks'),
  }));
  public form = this.formContainer.form;

  public saveHelper = new FormSaveHelper(this.formContainer, this.messageService);
  destroyRef = inject(DestroyRef);

  constructor(
    private formHelperService: FormHelperService,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private errorService: ErrorService,
    private apollo: Apollo,
  ) {}

  ngOnInit(): void {
    this.formHelperService
      .getData(
        this.formContainer.model,
        this.formContainer.queryFields,
        this.route.snapshot.params['newcomerId'],
      )
      .pipe(
        catchError(err => this.errorService.HandleGraphQLError('Laden van nieuwkomer gegevens')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(data => {
        const preparedData = this.formContainer.prepareDataForForm(notNull(data.newcomer));
        this.formHelperService.updateFormFromModel(this.form, preparedData);
      });

    // savingSignal, saveFunction, response$
    // arguments: afterValidation, afterPrepare, handleTransimissionErrors, handleServerErrors, showSuccessMessage

    this.saveHelper.onSave$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.retrieveUserRights();

    const addressControls = [
      this.form.controls.street,
      this.form.controls.houseNumber,
      this.form.controls.zip,
      this.form.controls.city,
      this.form.controls.willMoveSoon,
      this.form.controls.newStreet,
      this.form.controls.newHouseNumber,
      this.form.controls.newZip,
      this.form.controls.newCity,
    ];
    const addressChanges$ = merge(...addressControls.map(control => control.valueChanges));
    const addressChangeSubscription = addressChanges$.subscribe(output => {
      addressControls.forEach(control => control.setErrors(null));
    });

    this.subscriptions.add(addressChangeSubscription);
  }

  private retrieveUserRights() {
    const rightsSubscription = this.apollo
      .query({
        query: RIGHTS_QUERY,
      })
      .pipe(catchError(() => this.errorService.HandleGraphQLError('Ophalen van gebruikersrechten')))
      .subscribe(output => {
        this.userHasRightsToSensitiveData = output.data.me.managerHasAccessToSensitiveMessages;
      });

    this.subscriptions.add(rightsSubscription);
  }

  assertBooleanFieldType(fieldInfo: FieldInputType) {
    if (fieldInfo.type !== 'boolean') {
      throw new Error('Field is not a boolean field');
    }
    return fieldInfo as BooleanFieldTypeInfo;
  }

  speaksOtherLanguages(): boolean {
    return this.form.value.languages?.includes(this.languageOptions.OTHER) ?? false;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
